import { useTranslation } from "@/providers/language-provider";
import { RotateCw, Search, User, UserPlus } from "lucide-react";
import { UserButton, UserSkeleton } from "@/features/user-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useRef, useState } from "react";
import { showMessageBox } from "@/components/blocks/message-box";
import { graphQLClient } from "@/lib/client-graphql";
import { useChats } from "@/providers/chat-provider";
import { OpenProfile } from "@/pages/layouts/main-header";

export const AddFriend = () => {
  const { t } = useTranslation();
  const { chats } = useChats();
  const [isSearching, setIsSearching] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [sentRequests, setSentRequests] = useState<any[]>([]);
  const [sendingRequest, setSendingRequest] = useState<string | null>(null);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);

    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    // 이전 타이머가 있다면 취소
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // 새로운 타이머 설정 - 마지막 입력 후 500ms 기다림
    timeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      const result = await graphQLClient.searchUser({ search: term }, true);
      const filteredFriends = result.filter(
        (user: any) =>
          !chats.friends.some((chat: any) => chat.friend._id === user._id),
      );
      setSearchResults(filteredFriends);
      setSentRequests([]);
      setIsSearching(false);
    }, 500);
  };

  const handleAddFriend = async (friend: any) => {
    const choice = await showMessageBox({
      title: t("AddFriend"),
      description: t("AddFriendRequest", { name: friend.name }),
    });
    if (choice === "Yes") {
      try {
        setSendingRequest(friend._id);
        const result = await graphQLClient.sendFriendRequest({
          userId: friend._id,
        });
        setSentRequests((prevRequests: any) => [...prevRequests, friend._id]);
        console.log("친구 요청을 보냈습니다.", result);
      } catch (error: any) {
        showMessageBox({
          type: "Error",
          title: "Error",
          description: `[${error.cause?.extensions?.code}] ${error.message}`,
        });
      } finally {
        setSendingRequest(null);
      }
    }
  };
  return (
    <div className="flex flex-col w-full h-full px-4 py-2 space-y-4">
      <div className="relative">
        <Search className="absolute left-2 top-3 h-4 w-4 text-gray-500" />
        <Input
          placeholder={t("SearchUserPlaceHolder")}
          value={searchTerm}
          onChange={handleSearch}
          className="pl-8"
        />
        {isSearching && (
          <RotateCw className="absolute right-2 top-3 h-4 w-4 animate-spin" />
        )}
      </div>

      {isSearching && (
        <div className="space-y-2 p-2">
          <UserSkeleton />
          <UserSkeleton />
        </div>
      )}

      <ScrollArea className="flex-1">
        {searchResults.map((user: any) => {
          const aleadySent = sentRequests.includes(user._id);
          return (
            <UserButton asButton={false} key={user._id} user={user}>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => OpenProfile(user)}
                  disabled={aleadySent}
                >
                  <User className="h-4 w-4 mr-2" />
                  {t("Profile")}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleAddFriend(user)}
                  disabled={aleadySent}
                >
                  {user._id === sendingRequest ? (
                    <RotateCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4 mr-2" />
                  )}

                  {t("AddFriend")}
                </Button>
              </div>
            </UserButton>
          );
        })}
      </ScrollArea>
    </div>
  );
};
